import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Deliverability,
  LlmCostSeries,
  PhaseConversion,
  PipelineFunnel,
  ReplyRateSeries,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle, StatTile } from "@/components/Card";
import {
  ChartColors,
  ChartFrame,
  HorizontalBars,
  MultiLineChart,
  StackedBars,
} from "@/components/charts/Charts";

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

const STATUS_PALETTE: Record<string, string> = {
  NEW: "#94a3b8",
  CONTACTED: "#60a5fa",
  ENGAGED: "#3b82f6",
  QUALIFIED: "#2563eb",
  NEGOTIATING: "#9333ea",
  CLOSING: "#7c3aed",
  WON: "#16a34a",
  LOST: "#dc2626",
  UNRESPONSIVE: "#a3a3a3",
  RE_ENGAGEMENT: "#f59e0b",
  ARCHIVED: "#737373",
};

const DEFAULT_RANGE_DAYS = 30;
const RANGE_OPTIONS = [7, 30, 90, 180];

export default function AnalyticsPage() {
  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });
  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_RANGE_DAYS);

  // Pick first strategy when list loads. The non-null assertion is safe
  // because we just checked length > 0; tsconfig "noUncheckedIndexedAccess"
  // is on so the bracket access is otherwise typed as `T | undefined`.
  useEffect(() => {
    if (strategyId) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategyId(first.id);
  }, [strategiesQ.data, strategyId]);

  const range = useMemo(() => ({
    from: isoDaysAgo(windowDays),
    to: isoDaysAgo(0),
  }), [windowDays]);

  const enabled = !!strategyId;
  const qs = enabled ? `strategyId=${strategyId}&from=${range.from}&to=${range.to}` : "";

  const funnelQ = useQuery({
    queryKey: ["analytics-funnel", strategyId],
    enabled,
    queryFn: () => api.get<PipelineFunnel>(`/api/v1/analytics/pipeline?strategyId=${strategyId}`),
  });
  const replyQ = useQuery({
    queryKey: ["analytics-reply-rate", strategyId, range.from, range.to],
    enabled,
    queryFn: () => api.get<ReplyRateSeries>(`/api/v1/analytics/reply-rate?${qs}`),
  });
  const phaseConvQ = useQuery({
    queryKey: ["analytics-phase-conversion", strategyId, range.from, range.to],
    enabled,
    queryFn: () => api.get<PhaseConversion>(`/api/v1/analytics/phase-conversion?${qs}`),
  });
  const costQ = useQuery({
    queryKey: ["analytics-llm-cost", strategyId, range.from, range.to],
    enabled,
    queryFn: () => api.get<LlmCostSeries>(`/api/v1/analytics/llm-cost?${qs}`),
  });
  const deliverQ = useQuery({
    queryKey: ["analytics-deliverability", strategyId, range.from, range.to],
    enabled,
    queryFn: () => api.get<Deliverability>(`/api/v1/analytics/deliverability?${qs}`),
  });

  if (strategiesQ.isLoading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (strategiesQ.error || !strategiesQ.data || strategiesQ.data.length === 0) {
    return <div className="text-sm text-[var(--fg-muted)]">No strategies configured.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Pipeline, conversion, deliverability, LLM cost.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-[var(--fg-muted)]">Strategy</label>
          <select
            value={strategyId ?? ""}
            onChange={(e) => setStrategyId(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
          >
            {strategiesQ.data.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <label className="text-sm text-[var(--fg-muted)]">Window</label>
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
          >
            {RANGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} days</option>
            ))}
          </select>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Reply rate"
          value={replyQ.data ? `${replyQ.data.overallRatePct}%` : "—"}
          sub={replyQ.data ? `${replyQ.data.totalReplied} / ${replyQ.data.totalSent}` : undefined}
        />
        <StatTile
          label="Delivered rate"
          value={deliverQ.data ? `${deliverQ.data.deliveredRatePct}%` : "—"}
          sub={deliverQ.data
            ? `${deliverQ.data.delivered.toLocaleString()} / ${(deliverQ.data.sent).toLocaleString()}`
            : undefined}
        />
        <StatTile
          label="Bounce rate (per 1k)"
          value={deliverQ.data ? deliverQ.data.bouncePer1k : "—"}
          sub={deliverQ.data ? `${deliverQ.data.bounced.toLocaleString()} bounced` : undefined}
        />
        <StatTile
          label={`LLM spend (${windowDays}d)`}
          value={costQ.data ? `€${costQ.data.totalCostEur}` : "—"}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardTitle>Pipeline funnel</CardTitle>
          {funnelQ.data ? (
            <StackedBars
              totalLabel="prospects"
              rows={funnelQ.data.phases
                .slice()
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((p) => ({
                  label: p.phaseName,
                  total: p.prospectCount,
                  segments: Object.entries(p.byStatus).map(([status, count]) => ({
                    label: status,
                    value: count,
                    color: STATUS_PALETTE[status] ?? ChartColors.muted,
                  })),
                }))}
            />
          ) : (
            <div className="text-sm text-zinc-500">Loading…</div>
          )}
        </Card>

        <Card>
          <CardTitle>Phase conversion</CardTitle>
          {phaseConvQ.data ? (
            <HorizontalBars
              points={phaseConvQ.data.pairs.map((c) => ({
                label: `${c.fromPhaseSlug ?? "(initial)"} → ${c.toPhaseSlug}`,
                value: c.count,
              }))}
            />
          ) : (
            <div className="text-sm text-zinc-500">Loading…</div>
          )}
        </Card>
      </div>

      <ChartFrame title="Reply rate over time" sub={`From ${range.from} to ${range.to}`}>
        {replyQ.data ? (
          <MultiLineChart
            series={[
              {
                label: "Sent",
                color: ChartColors.muted,
                points: replyQ.data.points.map((p) => ({ x: p.date.slice(5), y: p.sent })),
              },
              {
                label: "Replied",
                color: ChartColors.positive,
                points: replyQ.data.points.map((p) => ({ x: p.date.slice(5), y: p.replied })),
              },
            ]}
          />
        ) : null}
      </ChartFrame>

      <ChartFrame title="LLM cost (€) by purpose">
        {costQ.data ? (
          <MultiLineChart
            series={Object.keys(costQ.data.totalByPurpose).map((purpose) => ({
              label: purpose,
              points: costQ.data!.points
                .filter((p) => p.purpose === purpose)
                .map((p) => ({ x: p.date.slice(5), y: parseFloat(p.costEur) })),
            }))}
            yLabel="€/day"
          />
        ) : null}
      </ChartFrame>
    </div>
  );
}
